import { MessageState, MessageType } from '../enums';
import { IArtifact } from './artifact.interfaces';

export interface IMessage {
    folderType?: string;
    flag: boolean;
    id: string;
    version: number;
    deliveryDate: string;
    messageType: MessageType;
    messageState: MessageState;
    folderId: string;
    mailboxId: string;
    note: string;
    read: boolean;
    readDate: string;
    receivedDate: string;
    senderId: string;
    senderOrganisationId: string;
    senderMessageId: string;
    senderName: string;
    senderUserId: string;
    title: string;
    trashed: boolean;
    trashedDate: string;
    legallyNotified: boolean;
    legallyNotifiedDateTime: string;
    artifacts?: Array<IArtifact>;
    recipient: IMessageRecipient;
    sentAtDate: string;
    updatedAtDate: string;
    createAtDate: string;
    reply: boolean;
    forward: boolean;
    replyByDateTime: string;
    replyData: IReplyData[];
    forwardData: IForwardData;
    folderName?: string;
    folderParentId?: string;
    lastContactPoint?: string | null;
    senderOrginalId?: string;
    senderIdType?: string;
    isForward?: boolean;
    isReply?: boolean;
}

export interface IReplyData {
    id: string;
    version: number;
    childMessageId: string;
    parentMessageId: string;
    messageIdentifier: string;
    senderId: string;
    recipientId: string;
    caseId: string;
    contactPointId: string;
    generatingSystemId: string;
    comment: string;
    additionalreplydata: IAdditionalReplyData[];
}

export interface IForwardData {
    id?: string;
    version?: number;
    originalMessageId?: string;
    originalSender?: string;
    originalContentResponsible?: string;
    contactPointId?: string;
    comment?: string;
    newMemoId?: string;
    originalMessageDateTime: Date | string;
}

export interface IAdditionalReplyData {
    id: string;
    version: number;
    label: string;
    value: string;
}

export interface IMessageReceipient {
    userId: number;
}

export interface IMessageList {
    items: Array<IMessage>;
    itemCount: number;
    totalCount: number;
}

export interface IMessagesRequest {
    mailboxId: string;
}

export interface IMessageDeleteRequest {
    id: string;
    version: number;
}

export interface IGetMessagesRequest extends IMessagesRequest {
    folderId: string;
    folderType: string;
    skipCount: number;
    takeCount: number;
    read: boolean;
    flag: boolean;
    searchValue: string;
    sortOrder: string;
    includeSigningRequests: boolean;
    language: string;
    senderId: string;
}

export interface ISelectedMessage {
    id: string;
    version: number;
}

export interface IMoveMessageRequest extends ISelectedMessage {
    folderId: string;
}

export interface IMoveMessagesRequest extends IMessagesRequest {
    messagesToMove: Array<IMoveMessageRequest>;
}

export interface IDeleteMessagesRequest extends IMessagesRequest {
    itemsToDelete: Array<ISelectedMessage>;
}

export interface IMessageNote {
    id: string;
    version: number;
    note: string;
}
export interface ICountItemCount {
    id: number;
}

export interface ICountItem {
    folderId: string;
    _count: ICountItemCount;
}

export interface IMailboxMessagesItem {
    mailboxId: string;
    _count: ICountItemCount;
}

export interface IUnreadCount {
    items: Array<ICountItem>;
    itemCount: number;
    unreadCount: number;
}

export interface IMailboxMessagesCount {
    items: Array<IMailboxMessagesItem>;
    itemCount: number;
}

export interface ITrashedUnreadCount {
    items: Array<ICountItem>;
    itemCount: number;
    trashedUnreadCount: number;
}

export interface IMessageRead {
    read: boolean;
    version: number;
}

export interface IMessagesRead extends IMessageRead {
    id: string;
}

export interface IMessageAddRemoveFlag {
    flag: boolean;
    version: number;
}

export interface IMessagesAddRemoveFlag extends IMessageAddRemoveFlag {
    id: string;
}

export interface IMessageLegalNotify {
    legallyNotified: boolean;
    version: number;
}

export interface IMessagesLegalNotify extends IMessageLegalNotify {
    id: string;
}

export const DragItemTypes = {
    MAILITEM: 'MailItem',
};

export interface IDragItem {
    sourceFolderId: string;
    documentTitle: string;
    documentId: string;
    version: number;
    msgState: MessageState;
    overBlocked: boolean;
    allowDragAndDrop: boolean;
    trashed: boolean;
}

export interface IGetMessagesResponse {
    messages: Array<IMessage>;
    messagesTotalCount: number;
}

export interface IMessageRecipientBase {
    id: string;
    version: number;
    recipientId: string;
    recipientIdType: string;
    label: string;
}

export interface IUpdateMessageRequest {
    id: string;
    version: number;
    folderId: string;
    label: string;
    flag?: boolean;
    legallyNotified?: boolean;
    read?: boolean;
    recipient?: IUpdateMessageRequestRecipient;
    note: string;
}
export interface IUpdateMessageActiveContactPointRequest {
    id: string;
    version: number;
    folderId: string;
    label: string;
    flag?: boolean;
    legallyNotified?: boolean;
    read?: boolean;
    recipient?: IUpdateMessageRequestRecipientActiveContactPoint;
    note: string;
}

export interface IDraftMessageDeleteRecipientUpdate {
    id: string;
    version: number;
    mailboxID: string;
    folderID: string;
    messageType: MessageType;
    messageState: MessageState;
    label: string;
    forward: boolean;
    reply: boolean;
    flag: boolean;
    legallyNotified: boolean;
    read: boolean;
    welcomeMessage: boolean;
    sender: {
        id: string;
        version: number;
        senderId: string;
        label: string;
        senderIdType: string;
    };
    recipient: string | null;
}

export interface IUpdateMessageRequestRecipient extends IMessageRecipientBase {
    contactPointId: string;
    contactGroup: string;
    label: string;
}
export interface IUpdateMessageRequestRecipientActiveContactPoint extends IUpdateMessageRequestRecipient {
    contactInfo: Array<IMessageContactInfo>;
}

export interface IMessageRecipient extends IMessageRecipientBase {
    contactPoint: IMessageContactPoint;
}

export interface IMessageContactPoint {
    id: string;
    version: number;
    contactPointId: string;
    contactGroup: string;
    label: string;
    contactInfo?: Array<IMessageContactInfo>;
}

export interface IMessageContactInfo {
    label: string;
    value: string;
}
